/**
 * نظام توزيع العملاء تلقائياً على السيرفرات
 * يختار السيرفر الأنسب بناءً على عدد العملاء النشطين
 *
 * 🔄 المرحلة الأخيرة (GoTrue-login-cutover، 2026-07-06): بيقرأ/يكتب دلوقتي
 * عبر GET/POST /api/super-admin/servers (Postgres daf_confirmation.
 * system_config، عبر الـ Express app) بدل Firestore مباشرة
 * (`doc(db,'system_config','servers')` + عدّ من `clients_v2` — كولكشن قديم/
 * مجمّد، الباك إند بيعدّ فعليًا من `clients_api` الحي عبر
 * `countActiveClientsForServer`). كل الحسابات (usagePercent/status/
 * availableSlots) بقت بتتحسب سيرفر-سايد في `getServersWithStats()`
 * (services/db.js) — نفس المعادلات بالظبط اللي كانت هنا.
 */
import { superAdminFetch } from './superAdminApi';

/**
 * جلب قائمة السيرفرات (raw config) + الإحصائيات المحسوبة سيرفر-سايد.
 */
async function fetchServersWithStats() {
  const data = await superAdminFetch('/servers');
  return { servers: data.servers || [], stats: data.stats || [] };
}

/**
 * جلب قائمة السيرفرات الخام (id/name/ip/port/domain/maxClients/priority) —
 * تستخدمها ServerManagement.js لعرض/تعديل الإعدادات.
 */
export async function getServersList() {
  const { servers } = await fetchServersWithStats();
  return servers;
}

/**
 * الحصول على أفضل سيرفر متاح (غير مستخدم حاليًا داخل هذا المشروع، لكن
 * سايبينه شغال لأي استهلاك مستقبلي — نفس منطق الأولوية/السعة القديم، لكن
 * بطلب HTTP واحد بدل قراءة Firestore لكل سيرفر على حدة).
 * @returns {Object} معلومات السيرفر المختار
 */
export async function getOptimalServer() {
  const { servers, stats } = await fetchServersWithStats();
  const statsById = Object.fromEntries(stats.map((s) => [s.id, s]));

  // ترتيب السيرفرات حسب الأولوية
  const sortedServers = [...servers].sort((a, b) => a.priority - b.priority);

  for (const server of sortedServers) {
    const activeClients = statsById[server.id]?.activeClients ?? 0;

    if (activeClients < server.maxClients) {
      return {
        server_id: server.id,
        server_name: server.name,
        server_url: `http://${server.ip}:${server.port}`,
        whatsapp_api_url: `https://${server.domain}/send`,
        current_load: activeClients,
        max_capacity: server.maxClients,
        available_slots: server.maxClients - activeClients,
      };
    }
  }

  // لو كل السيرفرات ممتلئة
  throw new Error('جميع السيرفرات ممتلئة! يرجى إضافة سيرفر جديد.');
}

/**
 * الحصول على إحصائيات جميع السيرفرات (نفس الـ shape القديم بالظبط —
 * id/name/domain/activeClients/maxClients/availableSlots/usagePercent/status)
 */
export async function getAllServersStats() {
  const { stats } = await fetchServersWithStats();
  return stats;
}

/**
 * تحديث قائمة السيرفرات (استبدال كامل — نفس دلالة `setDoc` القديمة بدون merge)
 */
export async function updateServers(serversList) {
  await superAdminFetch('/servers', {
    method: 'POST',
    body: JSON.stringify({ servers: serversList }),
  });
  console.log('Servers list updated successfully');
}

export default {
  getOptimalServer,
  getAllServersStats,
  getServersList,
  updateServers,
};
